import { z } from 'zod'

/**
 * The admin status page's one response (EPIC-31).
 *
 * - Architecture: docs/superpowers/specs/2026-10-18-admin-status-architecture-design.md §4.1
 * - Panel contents: docs/ux/2026-10-18-admin-status-ux.md §3
 *
 * The server computes each panel's `status` and `attention`, so the thresholds live in one tested
 * place. The client derives the overall verdict, the merged Needs attention list, and staleness
 * (now − `fetchedAt` against the panel's budget). A panel whose source failed is `unavailable`,
 * never `healthy`.
 */

/** Triage order (Joni §1): is it up, are users hitting errors, what changed, is usage normal */
export const panelNames = ['aws', 'sentry', 'deploy', 'posthog'] as const
export type PanelName = (typeof panelNames)[number]

/**
 * What a panel that answered can say. "Unknown" is never sent: the client derives it from an
 * `unavailable` panel or from staleness, so data never appears under an "Unavailable" word (Joni, #2381).
 */
export const panelStatuses = ['healthy', 'degraded', 'down'] as const
export type PanelStatus = (typeof panelStatuses)[number]

/**
 * How old a panel's `fetchedAt` may be before the client shows it stale (Joni §9.4). The server's
 * cache for each panel sits below its budget, so it never serves what the client would call stale.
 */
export const panelFreshnessMs: Record<PanelName, number> = {
  aws: 2 * 60_000,
  sentry: 5 * 60_000,
  deploy: 5 * 60_000,
  posthog: 15 * 60_000,
}

/** What an adapter can say about a failed source, so the Unavailable line can be specific (Joni §9.6) */
export const failureReasons = ['timeout', 'rate_limited', 'auth', 'not_configured', 'unavailable'] as const
export type FailureReason = (typeof failureReasons)[number]

const isoDateTime = z.iso.datetime()

export const attentionItemSchema = z.object({
  panel: z.enum(panelNames),
  /** `waiting` is a to-do, not a fault: an infra deploy waiting for approval (Joni §2) */
  severity: z.enum(['down', 'degraded', 'waiting']),
  title: z.string().describe('What needs attention, e.g. "Alarm firing: tunasong-entities API 5xx"'),
  since: isoDateTime.optional(),
  href: z.url().describe('The item in its source').optional(),
  anchor: z.string().describe('The row id in the panel, for links from Needs attention'),
})
export type AttentionItem = z.infer<typeof attentionItemSchema>

/** A tile's figure with its previous period, so the client can say `▲ 12 %` without guessing */
const tileSchema = z.object({ value: z.number().int().nonnegative(), previous: z.number().int().nonnegative() })

export const awsPanelSchema = z.object({
  summary: z.object({
    alarmsFiring: z.number().int().nonnegative(),
    stacksTotal: z.number().int().nonnegative(),
    stacksNotComplete: z.number().int().nonnegative(),
    functionsWithErrors: z.number().int().nonnegative(),
  }),
  alarms: z
    .object({
      name: z.string(),
      stack: z.string().optional(),
      region: z.string(),
      critical: z.boolean(),
      since: isoDateTime,
      href: z.url(),
    })
    .array(),
  /** Only stacks not in a `*_COMPLETE` state */
  stacks: z
    .object({ name: z.string(), region: z.string(), status: z.string(), updatedAt: isoDateTime, href: z.url() })
    .array(),
  /** Top 5 by error rate over the last hour, at least 20 invocations each */
  lambdaErrors: z
    .object({
      function: z.string().describe('stack / logical id, not the generated name'),
      invocations: z.number().int().nonnegative(),
      errors: z.number().int().nonnegative(),
      rate: z.number().min(0).max(1),
      href: z.url(),
    })
    .array(),
  health: z.object({
    /** Until the first event arrives, so an empty list isn't read as a long clean history */
    watchingSince: isoDateTime.optional(),
    events: z
      .object({
        service: z.string(),
        region: z.string(),
        status: z.enum(['open', 'upcoming']),
        startedAt: isoDateTime,
        href: z.url(),
      })
      .array(),
  }),
})

export const sentryPanelSchema = z.object({
  /** Past 100 the API is not paginated for an exact count; `capped` says "100+" */
  unresolved: z.object({ count: z.number().int().nonnegative(), capped: z.boolean() }),
  newIn24h: z.number().int().nonnegative(),
  regressedIn24h: z.number().int().nonnegative(),
  trend: z.object({
    /** 24 hourly buckets, oldest first */
    hourly: z.number().int().nonnegative().array().length(24),
    last24h: z.number().int().nonnegative(),
    previous24h: z.number().int().nonnegative(),
  }),
  /** New and regressed in 24 h, top 5 */
  issues: z
    .object({
      id: z.string(),
      title: z.string(),
      kind: z.enum(['new', 'regressed']),
      events: z.number().int().nonnegative(),
      users: z.number().int().nonnegative(),
      firstSeen: isoDateTime,
      href: z.url(),
    })
    .array(),
})

export const deployPanelSchema = z.object({
  stacks: z
    .object({
      stack: z.string(),
      status: z.enum(['succeeded', 'failed', 'in_progress']),
      deployedAt: isoDateTime,
      /** Matched from the GitHub deployment window (spec D6); absent when updated outside any run */
      commit: z
        .object({ sha: z.string(), matched: z.literal(true), prTitle: z.string().optional(), runHref: z.url() })
        .optional(),
    })
    .array(),
  ci: z.object({
    latest: z.object({ workflow: z.string(), conclusion: z.string(), at: isoDateTime, href: z.url() }).optional(),
    /** Over the last 10 runs on main */
    recent: z.object({ passed: z.number().int().nonnegative(), failed: z.number().int().nonnegative() }),
    runs: z.object({ id: z.string(), conclusion: z.string(), at: isoDateTime, href: z.url() }).array(),
  }),
  waiting: z.object({ workflow: z.string(), since: isoDateTime, href: z.url() }).array(),
})

export const posthogPanelSchema = z.object({
  activeUsers: z.object({ day: tileSchema, week: tileSchema, month: tileSchema }),
  /** `tracked: false` until the `signed_up` event ships (spec D7) */
  signUps: z.discriminatedUnion('tracked', [
    z.object({ tracked: z.literal(false) }),
    z.object({ tracked: z.literal(true), day: tileSchema, week: tileSchema }),
  ]),
  canary: z.object({
    lastHour: z.number().int().nonnegative(),
    sameHourLastWeek: z.number().int().nonnegative(),
  }),
  keyEvents: z
    .object({ event: z.string(), count: z.number().int().nonnegative(), previous: z.number().int().nonnegative() })
    .array(),
})

const panelEnvelope = <T extends z.ZodType>(data: T) =>
  z.discriminatedUnion('state', [
    z.object({
      state: z.literal('ok'),
      status: z.enum(panelStatuses),
      fetchedAt: isoDateTime,
      attention: attentionItemSchema.array(),
      data,
    }),
    z.object({
      state: z.literal('unavailable'),
      reason: z.enum(failureReasons),
      detail: z.string().describe("The source's own message, for the reason line").optional(),
      /** Shown below the reason, marked stale (Joni §3) */
      lastGood: z.object({ fetchedAt: isoDateTime, data }).optional(),
    }),
  ])

export const panelDataSchemas = {
  aws: awsPanelSchema,
  sentry: sentryPanelSchema,
  deploy: deployPanelSchema,
  posthog: posthogPanelSchema,
} as const

export const adminStatusSchema = z.object({
  generatedAt: isoDateTime,
  panels: z.object({
    aws: panelEnvelope(awsPanelSchema),
    sentry: panelEnvelope(sentryPanelSchema),
    deploy: panelEnvelope(deployPanelSchema),
    posthog: panelEnvelope(posthogPanelSchema),
  }),
})

/** `GET /status?panel=<name>`: one panel refetched, for its Try again (Joni §3). The client merges it in. */
export const adminPanelRefreshSchema = adminStatusSchema.extend({ panels: adminStatusSchema.shape.panels.partial() })

export type AdminStatus = z.infer<typeof adminStatusSchema>
export type AdminPanelRefresh = z.infer<typeof adminPanelRefreshSchema>
export type PanelData = { [P in PanelName]: z.infer<(typeof panelDataSchemas)[P]> }
export type PanelResult<P extends PanelName> = AdminStatus['panels'][P]
